import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button, Link, Typography } from '@one-thd/sui-atomic-components';
import { ProductResultsContext } from './ProductResultsContext';
import { getBrowseSearchParam, updateQueryParams, getRatingsMessage } from '../product-results-helpers';
import { getFirstNonEmptyObject } from '../utils/searchHelpers';

const ResultsHeader = ({ preamble, showShopAllLink, resultsRef, dataProp }) => {
  const { data: dataFromContext } = useContext(ProductResultsContext);
  const data = getFirstNonEmptyObject(dataFromContext, dataProp);

  const { metadata, searchReport, taxonomy } = data?.searchModel || {};
  const { contentType, h1Tag } = metadata || {};

  const { keyword, correctedKeyword, searchUrl } = searchReport || {};
  const keywordTerm = correctedKeyword || keyword;

  const { breadCrumbs = [] } = taxonomy || {};

  const ratingsMessage = useMemo(() => getRatingsMessage(breadCrumbs), [breadCrumbs]);
  const resultsKeyword = useMemo(
    () => h1Tag || keywordTerm || getBrowseSearchParam(breadCrumbs),
    [h1Tag, keywordTerm, breadCrumbs]
  );

  if (!data) return null;

  if (contentType !== 'categorypage' && (!searchReport || Object.keys(searchReport).length === 0)) {
    return null;
  }

  let skipSpellCheckUrl;

  if (correctedKeyword && searchUrl) {
    skipSpellCheckUrl = updateQueryParams(searchUrl,
      [{ paramName: 'skipSpellCheck', paramValue: 'true' }]);
  }

  const onShopAllClick = (event) => {
    event.preventDefault();
    let scrollUpto = resultsRef?.current?.parentElement?.previousSibling?.getBoundingClientRect().top;
    const scroll = window.pageYOffset || document.documentElement.scrollTop;
    if (scrollUpto) {
      const headerEle = document.querySelector('.Header3-sticky-desktop');
      scrollUpto -= headerEle ? headerEle.clientHeight : 0;
      window.scroll({
        top: scrollUpto + scroll,
        behavior: 'smooth'
      });
    }
  };

  return (
    <>
      <div className="sui-pt-4 sui-pb-0" data-component="ResultsHeader">
        <div className="sui-flex sui-flex-col sm:sui-flex-row sui-items-baseline">
          {preamble && (
            <Typography variant="h1" weight="display" color="primary" uppercase height="none">
              {preamble}&nbsp;
            </Typography>
          )}
          <Typography variant="h1" weight="display" color="primary" uppercase height="none">
            {resultsKeyword}
          </Typography>
          {showShopAllLink && (
            <Button
              variant="text"
              onClick={onShopAllClick}
              className="sui-ml-3 hover:sui-underline sui-text-lg"
              color="subtle"
            >
              Shop All
            </Button>
          )}
        </div>
      </div>
      {ratingsMessage && (
        <div className="sui-text-sm">
          {ratingsMessage}
        </div>
      )}
      {correctedKeyword && (
        <div className="sui-text-lg sui-leading-8">
          Search Instead for
          <Link href={skipSpellCheckUrl} className="sui-ml-1">
            {`  "${keyword}"`}
          </Link>
        </div>
      )}
    </>
  );
};

ResultsHeader.defaultProps = {
  preamble: null,
  showShopAllLink: false,
  resultsRef: {},
  dataProp: null
};

ResultsHeader.propTypes = {
  preamble: PropTypes.node,
  showShopAllLink: PropTypes.bool,
  resultsRef: PropTypes.shape({
    current: PropTypes.shape({
      parentElement: PropTypes.shape({
        previousSibling: PropTypes.shape({
          getBoundingClientRect: PropTypes.func
        })
      })
    })
  }),
  dataProp: PropTypes.shape({
    searchModel: PropTypes.shape({
      metadata: PropTypes.shape({
        contentType: PropTypes.string,
        h1Tag: PropTypes.string
      }),
      searchReport: PropTypes.shape({
        keyword: PropTypes.string,
        correctedKeyword: PropTypes.string,
        searchUrl: PropTypes.string
      }),
      taxonomy: PropTypes.shape({
        breadCrumbs: PropTypes.arrayOf(PropTypes.shape({
          label: PropTypes.string,
          url: PropTypes.string,
          browseUrl: PropTypes.string,
          dimensionName: PropTypes.string,
          refinementKey: PropTypes.string
        }))
      })
    })
  })
};

ResultsHeader.displayName = 'ResultsHeader';

export { ResultsHeader };
